import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  MinusIcon,
  ReceiptPercentIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import {
  MISService,
  ActionItem,
  Insight,
  InsightCategory,
  InsightPriority,
  Kpi,
  MisPeriod,
} from '../../services/mis.service';
import {
  badgeClass,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';

const PERIODS: { key: MisPeriod; label: string }[] = [
  { key: 'month', label: 'This month' },
  { key: 'quarter', label: 'This quarter' },
  { key: 'year', label: 'This financial year' },
];

const CATEGORY_LABELS: Record<InsightCategory, string> = {
  sales: 'Sales',
  inventory: 'Inventory',
  finance: 'Finance',
  operations: 'Operations',
  customer: 'Customers',
  supplier: 'Suppliers',
  production: 'Production',
};

const PRIORITY_META: Record<InsightPriority, { label: string; badge: string; icon: string }> = {
  critical: { label: 'Critical', badge: 'text-red-700 bg-red-50 ring-red-600/20', icon: 'text-red-600 bg-red-50' },
  high: { label: 'High', badge: 'text-orange-700 bg-orange-50 ring-orange-600/20', icon: 'text-orange-600 bg-orange-50' },
  medium: { label: 'Medium', badge: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20', icon: 'text-yellow-600 bg-yellow-50' },
  low: { label: 'Low', badge: 'text-gray-700 bg-gray-50 ring-gray-500/20', icon: 'text-blue-600 bg-blue-50' },
};

// Chart colours, the same two the home dashboard uses, plus a lighter indigo for the forecast
const SALES_COLOR = '#4f46e5';
const PURCHASE_COLOR = '#0d9488';
const FORECAST_COLOR = '#a5b4fc';

const parseDay = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const formatDay = (value: string) =>
  parseDay(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatMonth = (value: string) =>
  parseDay(`${value.slice(0, 7)}-01`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

const formatCompactMoney = (value: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', notation: 'compact', maximumFractionDigits: 1 }).format(
    Number(value || 0),
  );

const formatKpi = (value: number | null, unit: Kpi['unit']) => {
  if (value === null || value === undefined) return '—';
  if (unit === 'money') return formatMoney(value);
  if (unit === 'percent') return `${formatNumber(value, 1)}%`;
  if (unit === 'times') return `${formatNumber(value, 1)}×`;
  return formatNumber(value);
};

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const axisTick = { fontSize: 12, fill: '#6b7280' };

// A titled card around one chart, table or list
const Panel: React.FC<{
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, description, action, className, children }) => (
  <section className={cn('overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200', className)}>
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const EmptyState: React.FC<{ icon: React.ElementType; title: string; text: string }> = ({ icon: Icon, title, text }) => (
  <div className="flex h-64 flex-col items-center justify-center px-4 text-center">
    <Icon className="h-10 w-10 text-gray-300" />
    <p className="mt-2 text-sm font-medium text-gray-900">{title}</p>
    <p className="mt-1 text-sm text-gray-500">{text}</p>
  </div>
);

const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md bg-white px-3 py-2 text-sm shadow-lg ring-1 ring-gray-200">
      <p className="font-medium text-gray-900">{label}</p>
      {payload
        .filter((entry: any) => entry.value !== undefined && entry.value !== null)
        .map((entry: any) => (
          <p key={entry.dataKey} className="mt-1 flex items-center gap-2 text-gray-700">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: entry.color }} />
            {entry.name}
            <span className="ml-auto pl-4 font-medium tabular-nums text-gray-900">
              {entry.payload.isUnits ? formatNumber(entry.value) : formatMoney(entry.value)}
            </span>
          </p>
        ))}
    </div>
  );
};

const MISDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<MisPeriod>('month');
  const [categoryFilter, setCategoryFilter] = useState<InsightCategory | ''>('');

  const summaryQuery = useQuery({
    queryKey: ['mis', 'summary', period],
    queryFn: () => MISService.getExecutiveSummary(period),
    placeholderData: keepPreviousData,
  });
  const metricsQuery = useQuery({
    queryKey: ['mis', 'metrics', period],
    queryFn: () => MISService.getPerformanceMetrics(period),
    placeholderData: keepPreviousData,
  });
  const trendQuery = useQuery({
    queryKey: ['mis', 'trend'],
    queryFn: () => MISService.getTrendAnalysis(12),
  });
  const insightsQuery = useQuery({
    queryKey: ['mis', 'insights'],
    queryFn: () => MISService.getInsights(),
  });
  const actionsQuery = useQuery({
    queryKey: ['mis', 'recommendations'],
    queryFn: () => MISService.getStrategicRecommendations(),
  });

  const refreshMutation = useMutation({
    mutationFn: () => MISService.generateInsights(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['mis'] });
      toast.success(`Insights refreshed: ${result.active} found${result.retired ? `, ${result.retired} no longer apply` : ''}`);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not refresh the insights')),
  });

  const summary = summaryQuery.data;
  const allInsights = insightsQuery.data ?? [];
  const insights = categoryFilter ? allInsights.filter((insight) => insight.category === categoryFilter) : allInsights;
  const categoriesInUse = Array.from(new Set(allInsights.map((insight) => insight.category)));
  const urgentCount = allInsights.filter((insight) => insight.priority === 'critical' || insight.priority === 'high').length;

  const renderChange = (value: number | null | undefined, goodWhenUp = true) => {
    if (value === null || value === undefined) return <span className="text-xs text-gray-500">Nothing earlier to compare with</span>;
    const up = value >= 0;
    const Icon = up ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
    return (
      <span
        className={`inline-flex items-center text-xs font-medium ${value === 0 ? 'text-gray-500' : up === goodWhenUp ? 'text-green-700' : 'text-red-700'}`}
      >
        <Icon className="mr-1 h-3.5 w-3.5" />
        {formatNumber(Math.abs(value), 1)}% {up ? 'up' : 'down'} on the stretch before
      </span>
    );
  };

  const overdueOrders = summary ? summary.orders.overdueSales + summary.orders.overduePurchases + summary.orders.overdueWorkOrders : 0;
  const cards = [
    {
      label: 'Sales',
      value: formatMoney(summary?.revenue.current ?? 0),
      detail: `${formatNumber(summary?.revenue.orders ?? 0)} ${summary?.revenue.orders === 1 ? 'order' : 'orders'} · ${formatMoney(summary?.revenue.previous ?? 0)} before`,
      footer: renderChange(summary?.revenue.growth),
      icon: BanknotesIcon,
      tone: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Gross Margin (estimate)',
      value: summary?.grossMargin.percent == null ? '—' : `${formatNumber(summary.grossMargin.percent, 1)}%`,
      detail:
        summary?.grossMargin.percent == null
          ? 'No sales in this period yet'
          : `${formatMoney(summary.grossMargin.revenueExTax - summary.grossMargin.costOfGoods)} on ${formatMoney(summary.grossMargin.revenueExTax)} before tax`,
      footer: <span className="text-xs text-gray-500">At the cost prices products carry today</span>,
      icon: ReceiptPercentIcon,
      tone: 'text-teal-600 bg-teal-50',
    },
    {
      label: 'Awaiting Payment',
      value: formatMoney(summary?.receivables.outstanding ?? 0),
      detail: `${formatNumber(summary?.receivables.outstandingCount ?? 0)} unpaid ${summary?.receivables.outstandingCount === 1 ? 'invoice' : 'invoices'}`,
      footer: (
        <span className={`text-xs ${summary?.receivables.overdueCount ? 'font-medium text-red-700' : 'text-gray-500'}`}>
          {summary?.receivables.overdueCount
            ? `${formatMoney(summary.receivables.overdue)} overdue on ${formatNumber(summary.receivables.overdueCount)}`
            : 'Nothing is past its due date'}
        </span>
      ),
      icon: ClockIcon,
      tone: summary?.receivables.overdueCount ? 'text-red-600 bg-red-50' : 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Open Orders',
      value: formatNumber((summary?.orders.openSales ?? 0) + (summary?.orders.openPurchases ?? 0) + (summary?.orders.openWorkOrders ?? 0)),
      detail: `${formatNumber(summary?.orders.openSales ?? 0)} sales · ${formatNumber(summary?.orders.openPurchases ?? 0)} purchase · ${formatNumber(summary?.orders.openWorkOrders ?? 0)} production`,
      footer: (
        <span className={`text-xs ${overdueOrders ? 'font-medium text-red-700' : 'text-gray-500'}`}>
          {overdueOrders ? `${formatNumber(overdueOrders)} past the planned date` : 'None past the planned date'}
        </span>
      ),
      icon: ClipboardDocumentListIcon,
      tone: overdueOrders ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50',
    },
  ];

  const trend = trendQuery.data;
  const trendData = trend
    ? [
        ...trend.months.map((month) => ({ label: formatMonth(month.period), sales: month.sales, purchases: month.purchases })),
        ...trend.forecast.map((month) => ({ label: formatMonth(month.period), forecast: month.sales })),
      ]
    : [];
  const hasTrend = !!trend && trend.months.some((month) => month.sales > 0 || month.purchases > 0);
  const productionData = trend
    ? trend.months.map((month) => ({ label: formatMonth(month.period), production: month.production, isUnits: true }))
    : [];
  const hasProduction = productionData.some((month) => month.production > 0);

  const renderKpiDirection = (kpi: Kpi) => {
    if (kpi.value === null) return <span className="text-xs text-gray-500">Nothing to measure yet</span>;
    if (kpi.previous === null) return <span className="text-xs text-gray-500">As of now</span>;
    if (kpi.trend === 'stable') {
      return (
        <span className="inline-flex items-center text-xs font-medium text-gray-600">
          <MinusIcon className="mr-1 h-3.5 w-3.5" />
          Unchanged
        </span>
      );
    }
    const good = (kpi.trend === 'up') === kpi.higherIsBetter;
    const Icon = kpi.trend === 'up' ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
    return (
      <span className={`inline-flex items-center text-xs font-medium ${good ? 'text-green-700' : 'text-red-700'}`}>
        <Icon className="mr-1 h-3.5 w-3.5" />
        {good ? 'Better' : 'Worse'}
      </span>
    );
  };

  const renderInsight = (insight: Insight) => {
    const meta = PRIORITY_META[insight.priority];
    const Icon = insight.type === 'opportunity' || insight.type === 'trend' ? LightBulbIcon : ExclamationTriangleIcon;
    return (
      <li key={insight.id} className="flex gap-4 px-4 py-4">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${meta.icon}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">{insight.title}</h4>
            <span className={`${badgeClass} ${meta.badge}`}>{meta.label}</span>
            <span className={`${badgeClass} text-gray-700 bg-gray-50 ring-gray-500/20`}>{CATEGORY_LABELS[insight.category]}</span>
          </div>
          <p className="mt-1 text-sm text-gray-700">{insight.description}</p>
          {(insight.recommendations?.length ?? 0) > 0 && (
            <ul className="mt-2 space-y-1">
              {insight.recommendations!.map((text) => (
                <li key={text} className="flex gap-2 text-sm text-gray-600">
                  <ArrowRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                  {text}
                </li>
              ))}
            </ul>
          )}
        </div>
        {insight.metrics?.link && (
          <Link
            to={insight.metrics.link}
            className="hidden shrink-0 self-start rounded-md px-3 py-1.5 text-sm font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 sm:inline-flex"
          >
            {insight.metrics.linkLabel || 'Open'}
          </Link>
        )}
      </li>
    );
  };

  const renderActions = (title: string, hint: string, items: ActionItem[], icon: React.ElementType, tone: string) => {
    const Icon = icon;
    return (
      <div className="p-4">
        <div className="flex items-center gap-2">
          <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
            <p className="text-xs text-gray-500">{hint}</p>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">Nothing here right now.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {items.map((item) => (
              <li key={`${item.insight}-${item.text}`} className="text-sm">
                <p className="text-gray-800">{item.text}</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  From “{item.insight}”
                  {item.link && (
                    <>
                      {' · '}
                      <Link to={item.link} className="font-medium text-indigo-700 hover:text-indigo-900">
                        Open
                      </Link>
                    </>
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">MIS Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            {summary
              ? `${formatDay(summary.period.from)} to ${formatDay(summary.period.to)}, against ${formatDay(summary.period.previousFrom)} to ${formatDay(summary.period.previousTo)}`
              : 'How the business is doing, from the orders, stock and invoices on record'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg bg-gray-100 p-1" role="group" aria-label="Period">
            {PERIODS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                aria-pressed={period === entry.key}
                onClick={() => setPeriod(entry.key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium',
                  period === entry.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending} className={secondaryButtonClass}>
            <ArrowPathIcon className={cn('mr-2 h-4 w-4', refreshMutation.isPending && 'animate-spin')} />
            {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Insights'}
          </button>
        </div>
      </div>

      {/* Headline figures */}
      {summaryQuery.error ? (
        <div className="rounded-lg bg-white px-4 py-8 text-center shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-red-600">{getApiErrorMessage(summaryQuery.error, 'Could not load the figures')}</p>
        </div>
      ) : (
        <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', summaryQuery.isFetching && !summaryQuery.isLoading && 'opacity-60')}>
          {cards.map((card) => (
            <div key={card.label} className="flex items-start gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${card.tone}`}>
                <card.icon className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-gray-500">{card.label}</p>
                <p className="truncate text-xl font-semibold tabular-nums text-gray-900">{summaryQuery.isLoading ? '—' : card.value}</p>
                <p className="truncate text-xs text-gray-500">{summaryQuery.isLoading ? ' ' : card.detail}</p>
                <div className="mt-1">{summaryQuery.isLoading ? null : card.footer}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Insights */}
        <Panel
          title="What needs attention"
          description={
            insightsQuery.isLoading
              ? 'Loading...'
              : allInsights.length === 0
                ? 'Worked out from your data every hour.'
                : `${formatNumber(allInsights.length)} ${allInsights.length === 1 ? 'finding' : 'findings'}, ${formatNumber(urgentCount)} urgent. Worked out from your data every hour.`
          }
          className="xl:col-span-2"
          action={
            categoriesInUse.length > 1 ? (
              <select
                aria-label="Filter insights by area"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as InsightCategory | '')}
                className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">All areas</option>
                {categoriesInUse.map((category) => (
                  <option key={category} value={category}>
                    {CATEGORY_LABELS[category]}
                  </option>
                ))}
              </select>
            ) : undefined
          }
        >
          {insightsQuery.isLoading ? (
            <p className="px-4 py-12 text-center text-sm text-gray-500">Loading insights...</p>
          ) : insights.length === 0 ? (
            <EmptyState
              icon={CheckCircleIcon}
              title="Nothing needs attention"
              text="No stock, payment, delivery or sales problem was found in your data."
            />
          ) : (
            <ul className={cn('divide-y divide-gray-100', refreshMutation.isPending && 'opacity-60')}>{insights.map(renderInsight)}</ul>
          )}
        </Panel>

        {/* KPIs */}
        <Panel title="Key figures" description="This period against the stretch of the same length before it.">
          {metricsQuery.isLoading ? (
            <p className="px-4 py-12 text-center text-sm text-gray-500">Loading figures...</p>
          ) : (
            <ul className={cn('divide-y divide-gray-100', metricsQuery.isFetching && 'opacity-60')}>
              {(metricsQuery.data?.kpis ?? []).map((kpi) => (
                <li key={kpi.key} className="px-4 py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-gray-900">{kpi.name}</span>
                    <span className="text-base font-semibold tabular-nums text-gray-900">{formatKpi(kpi.value, kpi.unit)}</span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-500">
                      {kpi.previous !== null ? `Before: ${formatKpi(kpi.previous, kpi.unit)}` : kpi.description}
                    </span>
                    {renderKpiDirection(kpi)}
                  </div>
                  {kpi.previous !== null && <p className="mt-0.5 text-xs text-gray-500">{kpi.description}</p>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Action plan */}
      <Panel title="Action plan" description="What the findings above recommend, by how soon it needs doing.">
        {actionsQuery.isLoading ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">Loading actions...</p>
        ) : (
          <div className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {renderActions('Do now', 'From critical and high findings', actionsQuery.data?.immediate ?? [], ExclamationTriangleIcon, 'text-red-600 bg-red-50')}
            {renderActions('Do soon', 'From medium findings', actionsQuery.data?.shortTerm ?? [], ClockIcon, 'text-yellow-600 bg-yellow-50')}
            {renderActions('Keep in mind', 'From low findings', actionsQuery.data?.longTerm ?? [], LightBulbIcon, 'text-blue-600 bg-blue-50')}
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Sales and purchases */}
        <Panel
          title="Sales and purchases, last 12 months"
          description={trend?.forecast.length ? 'With a straight-line sales forecast for the next three months.' : (trend?.forecastNote ?? undefined)}
          className="xl:col-span-2"
        >
          {trendQuery.isLoading ? (
            <p className="px-4 py-12 text-center text-sm text-gray-500">Loading trend...</p>
          ) : !hasTrend ? (
            <EmptyState icon={ChartBarIcon} title="No orders in the last 12 months" text="Sales and purchases show here month by month." />
          ) : (
            <div className="h-72 p-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={formatCompactMoney} width={64} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f9fafb' }} />
                  <Legend iconType="square" formatter={(value) => <span className="text-sm text-gray-700">{value}</span>} />
                  <Bar dataKey="sales" name="Sales" fill={SALES_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                  <Bar dataKey="purchases" name="Purchases" fill={PURCHASE_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                  {trend!.forecast.length > 0 && (
                    <Bar dataKey="forecast" name="Sales forecast" fill={FORECAST_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        {/* Production */}
        <Panel title="Production, last 12 months" description="Units completed on work orders, by the month they finished.">
          {trendQuery.isLoading ? (
            <p className="px-4 py-12 text-center text-sm text-gray-500">Loading production...</p>
          ) : !hasProduction ? (
            <EmptyState
              icon={WrenchScrewdriverIcon}
              title="No work orders completed"
              text="Completed quantities show here once work orders are finished."
            />
          ) : (
            <div className="h-72 p-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productionData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={48} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f9fafb' }} />
                  <Bar dataKey="production" name="Units completed" fill={SALES_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default MISDashboard;
